/**
 * Admin Invites API client — invite-only registration management.
 * Calls GET /admin/applications, POST /admin/applications/:id/approve,
 * POST /admin/applications/:id/reject, GET /admin/invite-codes,
 * POST /admin/invite-codes.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type ApplicationStatus = "pending" | "approved" | "rejected";

export type AdminApplication = {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly reason: string;
  readonly status: ApplicationStatus;
  readonly rejection_reason: string | null;
  readonly created_at: string;
};

export type InviteCodeStatus = "available" | "claimed" | "expired";

export type AdminInviteCode = {
  readonly id: string;
  readonly code: string;
  readonly issued_by: string | null;
  readonly issued_by_name: string | null;
  readonly claimed_by: string | null;
  readonly claimed_by_name: string | null;
  readonly status: InviteCodeStatus;
  readonly created_at: string;
  readonly expires_at: string | null;
};

type ListApplicationsParams = {
  readonly status?: ApplicationStatus;
  readonly cursor?: string;
  readonly limit?: number;
};

type ListApplicationsResponse = {
  readonly items: AdminApplication[];
  readonly total: number;
  readonly next_cursor: string | null;
};

type ListInviteCodesParams = {
  readonly issued_by?: string;
  readonly status?: InviteCodeStatus;
  readonly cursor?: string;
  readonly limit?: number;
};

type ListInviteCodesResponse = {
  readonly items: AdminInviteCode[];
  readonly total: number;
  readonly next_cursor: string | null;
};

// ─── API Calls ───

export async function listApplications(
  params: ListApplicationsParams,
): Promise<ListApplicationsResponse> {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const res = await apiFetch<{ data: ListApplicationsResponse }>(
    qs ? `/admin/applications?${qs}` : "/admin/applications",
  );
  return res.data;
}

export async function approveApplication(id: string): Promise<void> {
  await apiFetch<{ data: { approved: boolean; invite_code_id: string } }>(
    `/admin/applications/${id}/approve`,
    { method: "POST" },
  );
}

export async function rejectApplication(
  id: string,
  reason?: string,
): Promise<void> {
  await apiFetch<{ data: { rejected: boolean } }>(
    `/admin/applications/${id}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? "" }),
    },
  );
}

export async function listInviteCodes(
  params: ListInviteCodesParams,
): Promise<ListInviteCodesResponse> {
  const sp = new URLSearchParams();
  if (params.issued_by) sp.set("issued_by", params.issued_by);
  if (params.status) sp.set("status", params.status);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const res = await apiFetch<{ data: ListInviteCodesResponse }>(
    qs ? `/admin/invite-codes?${qs}` : "/admin/invite-codes",
  );
  return res.data;
}

export async function createInviteCode(
  issuedBy?: string,
): Promise<AdminInviteCode> {
  const res = await apiFetch<{ data: { code: AdminInviteCode } }>(
    "/admin/invite-codes",
    {
      method: "POST",
      body: JSON.stringify({ issued_by: issuedBy ?? null }),
    },
  );
  return res.data.code;
}
